import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { UserArea as UserAreaComponent } from 'b2b-components/theme/components/Header/UserArea';
import { useCartStatusEffect } from 'b2b-apps/shop/cart/hooks';
import { useDeviceType } from 'b2b-apps/shop/common/hooks';
import { config } from 'b2b-apps/shop/config';
import { useCheckPermissions, useLoginAction, useLogoutAction, useUserData } from 'b2b-apps/shop/context/hooks';
import { getHasOnlyCoreAssortment } from 'b2b-apps/shop/context/redux/selectors';
import { PERMISSION } from 'b2b-apps/shop/context/utils/permissions';
import { useActiveLanguage, useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { useMyAccountDashboardLinks } from 'b2b-apps/shop/myAccount/common/hooks';
import { ROUTES } from 'b2b-apps/shop/routes/components/Router';
import { useRouterPush, useUrlCreator } from 'b2b-apps/shop/routes/hooks';
import { openLink, simulateLinkClick } from 'b2b-apps/shop/routes/utils/url';

interface Props {
  className?: string;
}

export const UserArea = ({ className }: Props): ReactElement => {
  const { isLoggedIn, debitorId } = useUserData();
  const { count: cartProductCount, value: cartValue } = useCartStatusEffect();
  const deviceType = useDeviceType();
  const push = useRouterPush();
  const urlCreator = useUrlCreator();
  const activeLanguage = useActiveLanguage();
  const hasOnlyCoreAssortment = useSelector(getHasOnlyCoreAssortment);
  const t = useTranslate();

  const hasAccessKanbanPermissions = useCheckPermissions([PERMISSION.accessKanban]);
  const hasAccessCartPermissions = useCheckPermissions([PERMISSION.accessCart]);
  const hasAccessWishlistPermissions = useCheckPermissions([PERMISSION.accessWishlist]);
  const hasMyAccountPermissions = useCheckPermissions([PERMISSION.accessMyAccountDashboard]);

  const hasExpressOrder = config.features.expressOrder && (hasAccessCartPermissions || hasAccessWishlistPermissions);
  const isSelfRegistrationEnabled = config.features.selfRegistration;
  const selfRegistrationUrl = isSelfRegistrationEnabled ? config.selfRegistrationUrl : '';
  const myAccountDashboardLinks = useMyAccountDashboardLinks();

  const handleLogin = useLoginAction();
  const handleLogout = useLogoutAction();
  const handleMyAccountLinkClick = (url: string) => openLink(url, push);
  const handleMyAccountClick = () => push(urlCreator(ROUTES.myAccount));
  const handleWishlistsClick = () => push(urlCreator(ROUTES.myAccountWishlists));
  const handleExpressOrderClick = () => push(urlCreator(ROUTES.expressOrder));
  const handleCartClick = () => push(urlCreator(ROUTES.cart));
  const handleKanbanClick = () => push(urlCreator(ROUTES.kanban));
  const handleRegisterClick = () => simulateLinkClick(
    selfRegistrationUrl.replace('{language}', activeLanguage),
  );

  return (
    <UserAreaComponent
      cartProductCount={cartProductCount}
      cartValue={cartValue}
      deviceType={deviceType}
      myAccountDashboardLinks={myAccountDashboardLinks}
      hasMyAccount={hasMyAccountPermissions}
      hasExpressOrder={hasExpressOrder}
      hasKanban={hasAccessKanbanPermissions}
      hasWishlists={hasAccessWishlistPermissions}
      hasOnlyCoreAssortment={hasOnlyCoreAssortment}
      isLoggedIn={isLoggedIn}
      isRegisterEnabled={isSelfRegistrationEnabled}
      onLogin={handleLogin}
      onLogout={handleLogout}
      onMyAccountClick={handleMyAccountClick}
      onMyAccountLinkClick={handleMyAccountLinkClick}
      onWishlistsClick={handleWishlistsClick}
      onExpressOrderClick={handleExpressOrderClick}
      onCartClick={handleCartClick}
      onKanbanClick={handleKanbanClick}
      onRegister={handleRegisterClick}
      text={{
        kanbanOrder: t('Kanban Order'),
        expressOrder: t('Express Order'),
        myAccount: t('My Account {debitorId}', { debitorId: debitorId ? `(${debitorId})` : '' }),
        login: t('Login'),
        logout: t('Logout'),
        wishlists: t('Wishlists'),
        register: t('Register'),
        coreAssortmentOnly: t('This account is restricted to core assortment only'),
      }}
      dataAttributes={{
        cartButton: { 'data-test-id': 'userAreaCartButton' },
        loginButton: { 'data-test-id': 'userAreaLoginButton' },
        logoutButton: { 'data-test-id': 'userAreaLogoutButton' },
      }}
      className={className}
    />
  );
};
